import React from 'react';
import styled from 'styled-components';
import ImpactSummarySection from './ImpactSummarySection';
import TrueImpactCapsule from 'shared/design-system/components/TrueImpactCapsule';
import { BottomBar } from '../BottomBar';
import { SectionBase } from '../SectionBase';
import { validateAllIndicators } from '../ImpactIndicator/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { IndicatorStageRenderProps } from '../ImpactIndicator/IndicatorStageRender';
import { IMPACT_SUMMARY_QUESTION_ID } from '../SectionMultiRadio';

const subTitle = <>Here’s a quick summary of your Impact Model</>;

const StyledIntro = styled.div``;

const helperText = (beneficiaryTerm?: string, programName?: string) => {
  const beneficiaryTermLowerCase =
    beneficiaryTerm?.toLowerCase() || 'beneficiaries';
  return (
    <StyledIntro>
      Does this accurately summarize how <i>{programName} </i>
      improves the lives of {beneficiaryTermLowerCase}? Feel free to edit if
      needed, then click Save & Continue to advance to the next step!
    </StyledIntro>
  );
};

const StyledDiv = styled.div`
  width: 100%;
  max-width: 972px;
  border-radius: 4px;
  border: 1px solid ${COLORS.grayLight};
  margin-top: ${SPACERS.small};
  margin-bottom: ${SPACERS.large};
  .header {
    border-radius: 4px 4px 0px 0px;
    background-color: ${COLORS.grayLight};
    padding: ${SPACERS.medium} 43px;
    .first {
      margin-right: 53px;
    }
    .last {
      margin-left: 119px;
    }
  }
`;

const ImpactSummary: React.FC<IndicatorStageRenderProps> = ({
  reportId,
  guidanceText,
  capacityDevelopmentEnabled,
  learnEnabled,
  actEnabled,
  defaultIndicators,
  selectedIndicators,
  unselectedIndicators,
  pageSectionProps,
  serviceGoal,
  serviceType,
  intermediaries,
  isReportPassed,
  deletedIndicators,
  beneficiaryTerm,
  programName,
  handleRestoreIndicator,
  handleBack,
  handleSaveAndContinue,
  handleAddIndicator,
  handleDeleteIndicator,
  handleIndicatorTextUpdate,
  handleDetailClose,
}) => {
  return (
    <SectionBase
      {...pageSectionProps}
      subTitle={subTitle}
      helpText={helperText(beneficiaryTerm, programName)}
      helpTextMaxWidth={620}
      questionId={IMPACT_SUMMARY_QUESTION_ID}
      guidanceText={guidanceText}
    >
      <StyledDiv>
        <div className="header">
          <TrueImpactCapsule className="first">Stage</TrueImpactCapsule>
          <TrueImpactCapsule>Indicators</TrueImpactCapsule>
          <TrueImpactCapsule className="last">
            What does this indicator mean to your program?
          </TrueImpactCapsule>
        </div>
        {capacityDevelopmentEnabled &&
          defaultIndicators.capacityDevelopment.length !== 0 && (
            <ImpactSummarySection
              reportId={reportId}
              sectionKey="capacityDevelopment"
              text="Program Development"
              bottomBorder={true}
              beneficiaryTerm={beneficiaryTerm}
              programName={programName}
              serviceGoal={serviceGoal}
              serviceType={serviceType}
              intermediaries={intermediaries}
              isReportPassed={isReportPassed}
              defaultIndicators={defaultIndicators.capacityDevelopment}
              selectedIndicators={selectedIndicators.capacityDevelopment}
              unselectedIndicators={unselectedIndicators.capacityDevelopment}
              deletedIndicators={deletedIndicators.capacityDevelopment}
              handleRestoreIndicator={handleRestoreIndicator}
              handleAdd={handleAddIndicator}
              handleDelete={handleDeleteIndicator}
              handleIndicatorTextUpdate={handleIndicatorTextUpdate}
            />
          )}
        {defaultIndicators.reach.length !== 0 && (
          <ImpactSummarySection
            reportId={reportId}
            sectionKey="reach"
            text="Reach"
            bottomBorder={true}
            beneficiaryTerm={beneficiaryTerm}
            programName={programName}
            serviceGoal={serviceGoal}
            serviceType={serviceType}
            intermediaries={intermediaries}
            isReportPassed={isReportPassed}
            defaultIndicators={defaultIndicators.reach}
            selectedIndicators={selectedIndicators.reach}
            unselectedIndicators={unselectedIndicators.reach}
            deletedIndicators={deletedIndicators.reach}
            handleRestoreIndicator={handleRestoreIndicator}
            handleAdd={handleAddIndicator}
            handleDelete={handleDeleteIndicator}
            handleIndicatorTextUpdate={handleIndicatorTextUpdate}
          />
        )}
        {learnEnabled && defaultIndicators.learn.length !== 0 && (
          <ImpactSummarySection
            reportId={reportId}
            sectionKey="learn"
            text="Learn"
            bottomBorder={true}
            beneficiaryTerm={beneficiaryTerm}
            programName={programName}
            serviceGoal={serviceGoal}
            serviceType={serviceType}
            intermediaries={intermediaries}
            isReportPassed={isReportPassed}
            defaultIndicators={defaultIndicators.learn}
            selectedIndicators={selectedIndicators.learn}
            unselectedIndicators={unselectedIndicators.learn}
            deletedIndicators={deletedIndicators.learn}
            handleRestoreIndicator={handleRestoreIndicator}
            handleAdd={handleAddIndicator}
            handleDelete={handleDeleteIndicator}
            handleIndicatorTextUpdate={handleIndicatorTextUpdate}
          />
        )}
        {actEnabled && defaultIndicators.act.length !== 0 && (
          <ImpactSummarySection
            reportId={reportId}
            sectionKey="act"
            text="Act"
            bottomBorder={true}
            beneficiaryTerm={beneficiaryTerm}
            programName={programName}
            serviceGoal={serviceGoal}
            serviceType={serviceType}
            intermediaries={intermediaries}
            isReportPassed={isReportPassed}
            defaultIndicators={defaultIndicators.act}
            selectedIndicators={selectedIndicators.act}
            unselectedIndicators={unselectedIndicators.act}
            deletedIndicators={deletedIndicators.act}
            handleRestoreIndicator={handleRestoreIndicator}
            handleAdd={handleAddIndicator}
            handleDelete={handleDeleteIndicator}
            handleIndicatorTextUpdate={handleIndicatorTextUpdate}
          />
        )}
        {defaultIndicators.succeed.length !== 0 && (
          <ImpactSummarySection
            reportId={reportId}
            sectionKey="succeed"
            text="Succeed"
            beneficiaryTerm={beneficiaryTerm}
            programName={programName}
            serviceGoal={serviceGoal}
            serviceType={serviceType}
            intermediaries={intermediaries}
            isReportPassed={isReportPassed}
            defaultIndicators={defaultIndicators.succeed}
            selectedIndicators={selectedIndicators.succeed}
            unselectedIndicators={unselectedIndicators.succeed}
            deletedIndicators={deletedIndicators.succeed}
            handleRestoreIndicator={handleRestoreIndicator}
            handleAdd={handleAddIndicator}
            handleDelete={handleDeleteIndicator}
            handleIndicatorTextUpdate={handleIndicatorTextUpdate}
          />
        )}
      </StyledDiv>
      <BottomBar
        disabled={false}
        hideBack={false}
        hideNext={
          !validateAllIndicators(
            selectedIndicators,
            capacityDevelopmentEnabled,
            learnEnabled,
            actEnabled,
          )
        }
        handleBack={handleDetailClose}
        handleNext={handleSaveAndContinue}
        backTitle="Back"
        nextTitle="Save & Continue"
      />
    </SectionBase>
  );
};

export default ImpactSummary;
